/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import Autocomplete from "@mui/material/Autocomplete";
import type {
  AutocompleteProps as MuiAutocompleteProps,
  AutocompleteRenderOptionState,
  AutocompleteOwnerState,
  AutocompleteValue,
  AutocompleteRenderInputParams,
} from "@mui/material/Autocomplete";
import type { AutocompleteFreeSoloValueMapping } from "@mui/material/useAutocomplete";
import React, { forwardRef, isValidElement, useRef, useState, useEffect, useMemo } from "react";
import axiosWrapper from "../../services/axiosWrapper";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { untranslated } from "@i18n";

// ===== Type Definitions =====

/** Base constraint for option objects - must be a plain object with string keys */
type OptionBase = Record<string, unknown>;

/** Shape of the response from remote data source */
interface RemoteDataResponse {
  data: { result: unknown[] };
}

const defaultOptionStyle: React.CSSProperties = {
  borderTop: "1px solid #ECF3FE",
  height: "3rem",
  padding: "0 1rem",
  whiteSpace: "nowrap",
  overflow: "hidden",
  lineHeight: "3rem",
  display: "block",
  textOverflow: "ellipsis",
  transition: "background-color 300ms ease-in-out",
};

/**
 * Props for the AutoComplete component.
 *
 * Extends MUI AutocompleteProps with:
 * - `valueField` for string-to-option resolution
 * - `remoteDataSource` / `query` / `beforeDataBound` for remote data fetching
 * - `width` for convenience sizing
 *
 * @template T - Option type (object or string)
 * @template Multiple - Whether multiple values can be selected
 * @template DisableClearable - Whether the clear button is hidden
 * @template FreeSolo - Whether arbitrary string input is allowed
 */
interface AutoCompleteProps<
  T extends OptionBase | string = OptionBase,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
> extends Omit<
  MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
  "options" | "value" | "defaultValue" | "renderInput"
> {
  // ── Custom props (not in MUI) ──

  /**
   * Field name on option objects to match string values against.
   * When set, `value="abc"` resolves to `options.find(o => o[valueField] === "abc")`.
   * Only valid when options are objects (not strings).
   */
  valueField?: T extends OptionBase ? Extract<keyof T, string> : never;

  /** URL endpoint for fetching options via POST */
  remoteDataSource?: string;

  /** Query parameters sent to the remote data source */
  query?: Record<string, unknown>;

  /** Transform the API response before setting options */
  beforeDataBound?: (response: RemoteDataResponse, query: Record<string, unknown>) => T[];

  /** Convenience prop for setting the component width */
  width?: string | number;

  minWidth?: string | number;

  /** Custom styles to merge into each option's inline style */
  listStyle?: React.CSSProperties;

  // ── Overridden MUI props ──

  /** Options list — optional because remoteDataSource can provide them instead */
  options?: readonly T[];

  /**
   * The selected value. When `valueField` is set, can also be a string
   * that will be resolved against `option[valueField]`.
   */
  value?: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo> | (T extends OptionBase ? string : never);

  /** Default value. When `valueField` is set, can also be a string. */
  defaultValue?: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo> | (T extends OptionBase ? string : never);

  /** Render the input element (required — MUI requires it and this component provides no default) */
  renderInput: (params: AutocompleteRenderInputParams) => React.ReactNode;
}

// ===== Component Implementation =====

function AutoCompleteComponent<
  T extends OptionBase | string = OptionBase,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>(
  props: AutoCompleteProps<T, Multiple, DisableClearable, FreeSolo>,
  ref: React.ForwardedRef<HTMLDivElement>,
): React.ReactElement {
  const btnRef = useRef<HTMLDivElement | null>(null);
  const resolvedRef = (ref || btnRef) as React.Ref<HTMLDivElement>;
  const [isLoading, setIsLoading] = useState(false);

  const {
    sx = {} as NonNullable<typeof props.sx>,
    remoteDataSource,
    query,
    options,
    beforeDataBound,
    width,
    getOptionLabel,
    renderOption,
    valueField,
    value,
    defaultValue,
    loading,
    listStyle = {},
    isOptionEqualToValue: isOptionEqualToValueProp,
    disableClearable,
    minWidth,
    ...otherProps
  } = props;

  const [optionsState, setOptionsState] = useState<T[]>(() => (Array.isArray(options) ? [...options] : []));

  useEffect(() => {
    try {
      if (Array.isArray(options)) {
        setOptionsState([...options]);
      } else if (!remoteDataSource && !query) {
        throw new Error("options should be an array or use remote data source");
      }
    } catch (e) {
      console.log(e);
      Promise.reject(e);
    }
  }, [options]);

  type MuiValue = AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>;

  const valueMemo = useMemo((): MuiValue | undefined => {
    if (valueField && typeof value === "string") {
      const found = optionsState.find(
        (o) => typeof o === "object" && o !== null && (o as OptionBase)[valueField] === value,
      );
      return (found ?? null) as MuiValue;
    }
    return value as MuiValue | undefined;
  }, [value, valueField, optionsState]);

  const defaultValueMemo = useMemo((): MuiValue | undefined => {
    if (valueField && typeof defaultValue === "string") {
      const found = optionsState.find(
        (o) => typeof o === "object" && o !== null && (o as OptionBase)[valueField] === defaultValue,
      );
      return (found ?? null) as MuiValue;
    }
    return defaultValue as MuiValue | undefined;
  }, [defaultValue, valueField, optionsState]);

  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchOptions = async (queryParam: Record<string, unknown> | undefined = query): Promise<void> => {
    if (!queryParam || !remoteDataSource) return;
    setIsLoading(true);
    const response = await axiosWrapper.post<{ result: T[] }>(remoteDataSource, {
      value: { ...queryParam, params: queryParam },
    });
    if (!mountedRef.current) return;
    const optionsData: T[] = beforeDataBound
      ? beforeDataBound(response as unknown as RemoteDataResponse, queryParam)
      : response.data.result;
    setOptionsState(optionsData);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchOptions();
  }, [query]);

  type OptionOrFreeSolo = T | AutocompleteFreeSoloValueMapping<FreeSolo>;

  const safeGetOptionLabel = useMemo(() => {
    if (getOptionLabel) {
      return (option: OptionOrFreeSolo): string => {
        if (option == null) return "";
        return getOptionLabel(option) ?? "";
      };
    }
    return (option: OptionOrFreeSolo): string => {
      if (option == null) return "";
      if (typeof option === "string") return option;
      return ((option as OptionBase)?.label as string) ?? "";
    };
  }, [getOptionLabel]);

  const mergedOptionStyle: React.CSSProperties = useMemo(() => ({ ...defaultOptionStyle, ...listStyle }), [listStyle]);

  const resolvedRenderOption = useMemo(() => {
    return function RenderOption(
      liProps: React.HTMLAttributes<HTMLLIElement> & { key: React.Key },
      option: T,
      state: AutocompleteRenderOptionState,
      ownerState: AutocompleteOwnerState<T, Multiple, DisableClearable, FreeSolo>,
    ): React.ReactNode {
      if (renderOption) {
        const result = renderOption(liProps, option, state, ownerState);
        if (isValidElement(result) && result.type === "li") {
          return result;
        }
        return (
          <ToolTipComponent
            slotProps={{ popper: { sx: { zIndex: 10000 } } }}
            content={untranslated(typeof result === "string" ? result : "")}
          >
            <li {...liProps} style={mergedOptionStyle}>
              {result}
            </li>
          </ToolTipComponent>
        );
      }
      const content = safeGetOptionLabel(option);
      return (
        <ToolTipComponent slotProps={{ popper: { sx: { zIndex: 10000 } } }} content={untranslated(content ?? "")}>
          <li {...liProps} style={mergedOptionStyle}>
            {content}
          </li>
        </ToolTipComponent>
      );
    };
  }, [renderOption, safeGetOptionLabel, mergedOptionStyle]);

  const resolvedIsOptionEqualToValue = useMemo((): ((option: T, val: T) => boolean) | undefined => {
    if (isOptionEqualToValueProp) return isOptionEqualToValueProp;
    if (valueField) {
      return (option: T, val: T): boolean => {
        if (typeof option === "object" && typeof val === "object") {
          return (option as OptionBase)?.[valueField] === (val as OptionBase)?.[valueField];
        }
        return option === val;
      };
    }
    return undefined;
  }, [isOptionEqualToValueProp, valueField]);

  const isControlled = value !== undefined;

  return (
    <Autocomplete<T, Multiple, DisableClearable, FreeSolo>
      ref={resolvedRef}
      sx={{
        minWidth: minWidth ?? "12.5rem",
        width: width || "auto",
        "& .MuiInputBase-root": { padding: "0.125rem 0.375rem", background: "white !important" },
        "& .MuiInputBase-root.Mui-disabled": { background: "#f5f5f5 !important" },
        "& .MuiInputBase-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgba(0, 0, 0, 0.26) !important",
        },
        ...(sx as Record<string, unknown>),
      }}
      slotProps={{ listbox: { sx: { padding: 0 } } }}
      {...otherProps}
      disableClearable={(disableClearable ?? true) as DisableClearable}
      {...(isControlled
        ? { value: valueMemo ?? (otherProps.multiple ? ([] as unknown as MuiValue) : (null as MuiValue)) }
        : { defaultValue: defaultValueMemo as MuiValue })}
      loading={isLoading || loading}
      options={optionsState}
      getOptionLabel={safeGetOptionLabel}
      renderOption={resolvedRenderOption}
      {...(resolvedIsOptionEqualToValue ? { isOptionEqualToValue: resolvedIsOptionEqualToValue } : {})}
    />
  );
}

const AutoComplete = forwardRef(AutoCompleteComponent) as <
  T extends OptionBase | string = OptionBase,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>(
  props: AutoCompleteProps<T, Multiple, DisableClearable, FreeSolo> & React.RefAttributes<HTMLDivElement>,
) => React.ReactElement | null;

(AutoComplete as { displayName?: string }).displayName = "AutoComplete";

export default AutoComplete;
export type { AutoCompleteProps, OptionBase };
